import moment from "moment";
import { WandaBookingStatus } from "./ApplicationTypes";

export type TTripAddress = {
  address1?: string;
  address2?: string;
  driverInstruction?: string;
  state?: string;
  city?: string;
  zip?: string;
  county?: string;
  strAddress?: string;
  isHomeAddress?: boolean;
  isDriverInStructionEnabled?: boolean;
  latitude?: string;
  longitude?: string;
};

export enum TripTypesButton {
  Inital = "inital",
  ReturnTrip = "returnTrip",
  AddStop = "addStop",
}

export enum TripType {
  ArrivedBy = "arrivedBy",
  LeaveAt = "leaveAt",
}

export enum AddressType {
  Pickup = "pickup",
  DropOff = "dropOff",
}

export type TTrip = {
  id: number;
  tripType: TripTypesButton;
  tripSelectedTypeDefault?: TripType;
  scheduleTime?: string;
  appointmentTime?: string;
  pickTime?: string;
  pickupAddressDefault?: TTripAddress;
  dropOffAddressDefault?: TTripAddress;
  firstName?: string;
  wheelChairTravel?: boolean;
  isWillCall?: boolean;
  totalCost?: number;
  distanceInMiles?: string;
  WandaCostDetail?: WandaCostDetail[];
  areTimesChronological?: Boolean;
  legCostDetails?: TripLegCost;
  vehicleType?: string;
  isPlus?: boolean;
  levelOfService?: number;
  grantTotal?: number;
  promoCode?: string;
  promoCodeAttributes?: string;
  redemptionId?: string;
  discountAmount?: number;
  discountValue?: number;
  discountType?: string;
  isValid?: boolean;
};

export type TripBothAddress = {
  [AddressType.Pickup]?: TTripAddress;
  [AddressType.DropOff]?: TTripAddress;
};

export type TDriverInstruction = {
  [AddressType.Pickup]: boolean;
  [AddressType.DropOff]: boolean;
};

export const defaultTripValue: TTrip = {
  id: 1,
  tripType: TripTypesButton.Inital,
  tripSelectedTypeDefault: TripType.LeaveAt,
  totalCost: 0,
  promoCode: "",
  promoCodeAttributes: "",
  redemptionId: "",
  discountAmount: 0,
  discountValue: 0,
  discountType: "",
};

export interface ITripRulesValidationInput {
  benefitGroupId: number;
  personId: number;
  rideRequests: RideRequest[];
  tripDate: string;
}

export interface RideRequest {
  dropOffAddress: Address;
  pickupAddress: Address;
  rideReasonId: number;
  scheduledTime: string;
}

export interface Address {
  address1: string;
  address2: string;
  city: string;
  county?: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
}

export interface IRulesValidationResponse {
  errors: Error[];
  gmr: Gmr;
  rulesValidationId: string;
  status: Status;
  transportationModes: TransportationMode[];
  services: RulesValidationServices[];
}

export interface RulesValidationServices {
  legId: number;
  miles: number;
  tripDate: string;
}

export interface Error {
  code: number;
  message: string;
}

export interface Gmr {
  estimatedReimbursementAmount: number;
  payees: Payee[];
}

export interface Payee {
  id: number;
  name: string;
  status: string;
}

export interface Status {
  downgradedModeIds: number[];
  pendingModeIds: number[];
  statusCode: number;
  validModeIds: number[];
}

export interface TransportationMode {
  cost: number[];
  hierarchy: number;
  id: number;
  name: string;
  providers: Provider[];
}

export interface Provider {
  providerId: number;
  providerName: string;
}

export type IAliasesCareRecivers = IAliasesCareReciver[];

export interface IAliasesCareReciver {
  businessKey: string;
  isActive: boolean;
  keyType: string;
  keyTypeId: number;
  organizationId: number;
  personAliasId: number;
  personId: number;
  sourceSystemId: number;
  sourceSystemLocation: string;
  sourceSystemType: string;
  isManagedMember: boolean;
  isSourceSystemNemtOrTransit: boolean;
  isSourceSystemTransit: boolean;
  isSourceSystemRevealNemt: boolean;
}

export interface ICreateReservationRequest {
  protocolInfo: IProtocolInfo;
  specialNeedsInfo?: ISpecialNeeds;
  trips: IReservationRequest[];
}
export interface IProtocolInfo {
  modeId: number;
  rulesValidationId: string;
}

export interface ISpecialNeeds {
  canTransfer: boolean;
  mobilityAids: string[];
  isWheelchair: boolean;
  wheelchairType: string;
  wheelchairWeight: string;
}

export interface IReservationRequest {
  benefitGroupOrganizationId: number;
  customerId: number;
  date: string;
  driverDropNotes: string;
  driverPickNotes: string;
  dropAddress1: string;
  dropAddress2: string;
  dropCity: string;
  dropState: string;
  dropZip: string;
  dropCounty: string;
  personId: number;
  pickAddress1: string;
  pickAddress2: string;
  pickCity: string;
  pickState: string;
  pickZip: string;
  pickCounty: string;
  tripPurposeID: number;
  isWillCall: boolean;
  scheduledTime: string;
  levelOfService: string;
  miles?: number;
  pickTime?: string;
  appointmentTime?: string;
}
export interface ICreateReservationResponse {
  reservationId: number;
  serviceRequestNumber: string;
  requestAssignmentId: number;
}

export enum CostDetails {
  PersonPerson = "Person-to-Person",
  DoorDoor = "Door-to-Door",
  CurbCurb = "Curb-to-Curb",
  WavRide = "WAV",
  WandaWav = "WandaWave",
  WandaStandard = "WandaStandard",
}

export interface ITripCostRequest {
  dropOffAddress: Address;
  pickupAddress: Address;
  distanceInMiles?: string;
}

export interface ITripStatusChangeData {
  customerId: number;
  date: string;
  mfTripStatusId: number;
  personId: number;
  reservationId: number;
  requestAssignmentId: number;
}

export interface ITripPaginationData {
  personId: number;
  wandaTripStatusIds?: number;
  rowsToSkip?: number;
  fetchRows?: number;
  fromDate?: string;
  toDate?: string;
  selectedPersonId?: string;
  sortColumn?: string;
  sortOrder?: string;
  skipPagination?: boolean;
}

export interface ITripEditFavorite {
  personId: number;
  name: string;
  bookingId: number[];
  requestAssignmentId: number;
}

export interface IMemberTrip {
  isAssignedProvider: boolean;
  isCancelled: boolean;
  actualDropPerformTime: any;
  actualPickPerformTime: any;
  appointmentDate: string;
  appointmentTime: any;
  cancelReasonId: any;
  clientPlanId: number;
  customerId: number;
  driverDropNotes: any;
  driverPickNotes: any;
  dropAddress1: string;
  dropAddress2: string;
  dropCity: string;
  dropCommonName: string;
  dropLatitude: number;
  dropLongitude: number;
  dropState: string;
  dropZip: string;
  dropCounty: string;
  fareTypeId: number;
  fareAmount: number;
  groupId: number;
  isPickPerformed: boolean;
  isDropPerformed: boolean;
  isWillCall: number;
  medicalFacilityName: any;
  mfTripId: number;
  mfTripStatusId: number;
  mode: string;
  modeId: number;
  parentReservationId: number;
  performStatus: string;
  pickAddress1: string;
  pickAddress2: string;
  pickCity: string;
  pickCommonName: string;
  pickLatitude: number;
  pickLongitude: number;
  pickState: string;
  pickZip: string;
  pickCounty: string;
  projectedTime: any;
  providerId: number;
  providerName: string;
  providerType: any;
  providerPhone: string;
  providerSuppliedScheduledPickUpTime: any;
  providerTripId: string;
  requestedPickTime: any;
  reservationId: number;
  reservationStatus: string;
  scheduledPickWindowBegin: string;
  scheduledPickWindowEnd: string;
  scheduledDropWindowBegin: string;
  scheduledDropWindowEnd: string;
  tripMiles: number;
  tripPurposeId: number;
  tripReason: string;
  tripStatus: string;
  vehicleDesc: any;
  vehicleId: any;
  vin: any;
  providerPickETA: any;
  providerDropETA: any;
  requestId: any;
  requestTypeId: any;
  requestNumber: any;
  reimbursementAmount: any;
  claimAmount: any;
  providerEnroute: any;
  clientPlanOrganizationId: any;
  hasFootPrint: boolean;
  reimbursementRequestViaMobile: any;
  requestAssignmentId: any;
  requestAssignmentNumber: any;
  organizationId: any;
  willCallReadyAt: any;
  sourceSystemId: number;
}

export interface ITripCostResponse {
  totalStandardCost: number;
  totalWaveCost: number;
  wandaPlusDiscount: number;
  wandaTripCost: WandaTripCost[];
  wandaMarketSourceSystemId: number;
}

export interface WandaTripCost {
  distanceInMiles: number;
  costDetails: WandaCostDetail[];
}

export interface WandaCostDetail {
  planName: string;
  rateDateCombo: string;
  effectiveDate: string;
  pickupFee: number;
  perMileRate: number;
  baseFee: number;
  losDoorToDoor: number;
  losPersonToPerson: number;
  totalRate: number;
}

export interface ILosCharges {
  losDoorToDoor: number;
  losPersonToPerson: number;
  losPersonToPersonWav: number;
}

export function DefaultLosCharges() {
  return {
    losDoorToDoor: 0,
    losPersonToPerson: 0,
    losPersonToPersonWav: 0,
  };
}

export interface IWandaTripInfo {
  wavExtraCost: number;
  wandaPlusDiscount: number;
  wandaMarketSourceSystemId: number;
}

export function DefaultWandaTripInfo() {
  return {
    wavExtraCost: 0,
    wandaPlusDiscount: 0,
    wandaMarketSourceSystemId: 0,
  };
}

export interface IUpdateTripCostRequest {
  tripDate: string;
  sourceSystemId: number;
  bookingId: number;
  tripCost: number[];
  personId: number;
  requestAssignmentId: number;
  paymentInstrumentId: number;
  wandaSubscriptionPlan: string;
  levelOfService: number;
}

export interface IUpdateTripResponse {
  isUpdated: boolean;
}

export interface ITripDateRange {
  startDate: string;
  endDate: string;
}

export function defaultUpcomingDateRange() {
  return {
    startDate: moment(Date.now()).format("MM/DD/YYYY"),
    endDate: moment()
      .add(30, "days")
      .format("MM/DD/YYYY"),
  };
}

export function defaultPastDateRange() {
  return {
    startDate: moment()
      .subtract(30, "days")
      .format("MM/DD/YYYY"),
    endDate: moment(Date.now()).format("MM/DD/YYYY"),
  };
}

export function defaultFavoriteDateRange() {
  return {
    startDate: "",
    endDate: "",
  };
}
export interface IWandaServiceArea {
  id: number;
  organizationId: number;
  zipCode: string;
  city: string;
  state: string;
  isActive: boolean;
}

export interface IDateFilterMobile {
  upcomingDateRangeFilter: ITripDateRange;
  pastDateRangeFilter: ITripDateRange;
}

export function DefaultDateFilterMobile() {
  return {
    upcomingDateRangeFilter: defaultUpcomingDateRange(),
    pastDateRangeFilter: defaultPastDateRange(),
  };
}

export interface IUpcomingFilters {
  upcomingCheckedTripTypes: Set<string>;
  upcomingCheckedServiceUsers: Set<string>;
  upcomingDateRangeFilter: ITripDateRange;
}

export function DefaultUpcomingFilters() {
  return {
    upcomingCheckedTripTypes: new Set(["Ride"]),
    upcomingCheckedServiceUsers: new Set(["All"]),
    upcomingDateRangeFilter: defaultUpcomingDateRange(),
  };
}

export interface IPastFilters {
  pastCheckedTripTypes: Set<string>;
  pastCheckedServiceUsers: Set<string>;
  pastDateRangeFilter: ITripDateRange;
}

export function DefaultPastFilters() {
  return {
    pastCheckedTripTypes: new Set(["Ride"]),
    pastCheckedServiceUsers: new Set(["All"]),
    pastDateRangeFilter: defaultPastDateRange(),
  };
}

export interface ICheckedOptionsMobile {
  checkedTripTypes: Set<string>;
  checkedServiceUsers: Set<string>;
}

export function DefaultCheckedOptionsMobile() {
  return {
    checkedTripTypes: new Set(["Ride"]),
    checkedServiceUsers: new Set(["All"]),
  };
}

export interface IExcelData {
  createdOn: moment.MomentInput;
  amount: string;
  paymentTransactionTypeName: string;
  paymentInstrumentName: string;
  paymentTransactionId: string;
  details: string;
  serviceType: string;
  serviceUser: string;
  tripId: number;
  bookingId: number;
  refundedTransactionId: number;
  subscriptionName: string;
}

export type ButtonTypes = {
  stopBtn: boolean;
  returnBtn: boolean;
  continueBtn: boolean;
  maxAdded?: boolean;
  skipReturnTrip: boolean;
  addReturnTrip: boolean;
};

export const ButtonTypeDefaultValue: ButtonTypes = {
  stopBtn: true,
  returnBtn: true,
  continueBtn: true,
  skipReturnTrip: false,
  addReturnTrip: false,
};

export type WheelchairUpdate = {
  personId: number;
  keyTypeId?: number;
  personAttributesKey?: string;
  personAttributesKeyId: number;
  wandaAccommodations: {
    wheelChairTravel: boolean;
    wheelChairPresent: boolean;
    wcToVehicleTransfer: boolean;
    personAttributesKeyId: number;
  };
};

export enum ModeSelection {
  Standard = "WandaStandard",
  WAV = "WandaWave",
}

type TripCreationAddresses = {
  dropOffAddress: {
    address1: string;
    address2: string;
    city: string;
    county?: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  };
  pickupAddress: {
    address1: string;
    address2: string;
    city: string;
    county?: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  };
  distanceInMiles: string;
};

export type WandaTripCreationDetails = {
  personId: number;
  careGiverPersonId: number;
  requestAssignmentId?: number;
  tripDate: string;
  levelOfService: number;
  vehicleType: string;
  isTripInitiated: boolean;
  paymentInstrumentId?: number;
  addressRequests: TripCreationAddresses[];
};

type TripLegCost = {
  transportationCost: number;
  vehicleTypeCost: number;
  levelOfServiceCost: number;
  wandaPlusDiscountCost: number;
  totalCost: number;
  miles: number;
  promoCode: string;
  promoCodeAttributes: string;
  redemptionId: string;
  discountAmount: number;
  discountValue: number;
  discountType: string;
};

export type WandaTripSummaryCostDetails = {
  vehicleType: string;
  isPlus: boolean;
  levelOfService: number;
  grantTotal: number;
  wandaTripLegDetails: TripLegCost[];
};

export const bookingStatus: { [key: string]: WandaBookingStatus } = {
  "1": WandaBookingStatus.Booked,
  "2": WandaBookingStatus.Canceled,
  "3": WandaBookingStatus.Completed,
};

export const dashboardRebookMessage: { [key: string]: string } = {
  "1": "Rebook - Dashboard Upcoming Services",
  "2": "Rebook - Dashboard Completed Services",
  "3": "Rebook - Dashboard Favorite Services",
};

export const SUProfileRebookMessage: { [key: string]: string } = {
  "1": "Rebook - SU Profile Upcoming Services",
  "2": "Rebook - SU Profile Completed Services",
  "3": "Rebook - SU Profile Favorite Services",
};

export enum SourcePageTypes {
  Dashboard = "Dashboard",
  SUProfile = "SUProfile",
}

export const LevelOfService: { [key: number]: string } = {
  1: "Person to Person",
  2: "Door to Door",
  3: "Curb to Curb",
};
